import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Paper,
  alpha,
  Stack,
  Divider,
} from '@mui/material';
import { reportStatuses, reportTypes, userCats } from '../../../../helpers/appstate';
import AllResultQaqc from './AllResultQaqc';
import { useEffect, useState } from 'react';
import { getYears } from '../../../../helpers/dateHelpers';
import { FilterAltOutlined, CalendarTodayOutlined } from '@mui/icons-material';
import { getFacilityByUserId, getStatePersonalizedV2, getStates } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import AllResultNormal from './AllResultNormal';

export default function AllResult({ props, history }) {
  const { userCategory, userPermission, userId } = props;
  const searchParams = new URLSearchParams(props?.location?.search);
  const years = getYears();
  const currentYear = new Date().getFullYear()?.toString();

  const reportquarters = [
    {
      value: 1,
      label: 'Q1',
    },
    {
      value: 2,
      label: 'Q2',
    },
    {
      value: 3,
      label: 'Q3',
    },
    {
      value: 4,
      label: 'Q4',
    },
  ];

  const planningStatuses = {
    //DRAFT: 'DRAFT',
    NOTPLANNED: 'NOT PLANNED',
    PLANNED: 'PLANNED',
    PLANNED_AND_SCHEDULED: 'Planned & Scheduled',
    ONGOING: 'ONGOING',
    SUBMITTED: 'SUBMITTED',
    APPROVED_BUT_NOT_PUBLISHED: 'Approved but not Published',
    PUBLISHED: 'PUBLISHED',
  };

  const year = searchParams.get('year') || currentYear;
  const type = searchParams.get('type') || 'qaqc';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const facilityId = searchParams.get('facility') || '';
  const stateId = searchParams.get('state') || '';
  const quarter = searchParams.get('quarter') || '1'; // Default parameter initialization string fallback
  const plannedStatus = searchParams.get('plannedStatus') || '';

  const initFacility = { facilityId: '', facilityName: '' };
  const initState = { stateId: '', stateName: '' };

  const [anchorFilterDrowpdownEl, setAnchorFilterDropdownMenuEl] = useState(null);
  const [resultStatusSelected, setResultStatusSelected] = useState(status || '');
  const [plannedStatusSelected, setPlannedStatusSelected] = useState(plannedStatus || '');


  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [stateList, setStateList] = useState([]);
  const [stateSelected, setStateSelected] = useState(initState);
  const [stateLoading, setStateLoading] = useState(false);

  useEffect(() => {
    handleInitYear();
    handleInitType();
    fetchFacilityList();
    fetchStateList();
  }, []);

  const handleFilterDropdownClick = (event) => setAnchorFilterDropdownMenuEl(event.currentTarget);
  const handleFilterDropdownClose = () => setAnchorFilterDropdownMenuEl(null);

  const handleInitYear = () => {
    if (!searchParams.get('year')) {
      const newParam = new URLSearchParams(props?.location?.search);
      newParam.set('year', currentYear);
      props?.history.push({ search: newParam.toString() });
    }
  };

  const handleInitType = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    let updated = false;

    if (!searchParams.get('type')) {
      newParam.set('type', reportTypes.QAQC?.toLowerCase());
      updated = true;
    }
    if (!searchParams.get('quarter')) {
      newParam.set('quarter', '1'); // Force default Quarter 1 state initialization
      updated = true;
    }

    if (updated) {
      props?.history.push({ search: newParam.toString() });
    }
  };

  const handleSelectResultYear = (selectedYear) => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.set('year', selectedYear);
    props?.history.push({ search: newParam.toString() });
  };

  const handleSelectResultType = (selectedType) => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.set('type', selectedType);

    // Maintained active parameter state fallback cleanly without data resets
    if (!newParam.get('quarter')) {
      newParam.set('quarter', '1');
    }
    props?.history.push({ search: newParam.toString() });
  };

  const handleSelectQuarter = (selectedQuarter) => {
    const newParam = new URLSearchParams(props?.location?.search);
    if (quarter === selectedQuarter.toString()) {
      newParam.delete('quarter');
    } else {
      newParam.set('quarter', selectedQuarter);
    }
    props?.history.push({ search: newParam.toString() });
  };

  const handleClearQuarter = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.delete('quarter');
    props?.history.push({ search: newParam.toString() });
  };

  const handleFilter = () => {
    const newParam = new URLSearchParams(props?.location?.search);

    if (resultStatusSelected) newParam.set('status', resultStatusSelected);
    else newParam.delete('status');

    if (plannedStatusSelected) newParam.set('plannedStatus', plannedStatusSelected);
    else newParam.delete('plannedStatus');

    if (facilitySelected?.facilityId) newParam.set('facility', facilitySelected?.facilityId);
    else newParam.delete('facility');

    if (stateSelected?.stateId) newParam.set('state', stateSelected?.stateId);
    else newParam.delete('state');

    props?.history.push({ search: newParam.toString() });
    handleFilterDropdownClose();
  };

  const handleClearReportStatus = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.delete('status');
    setResultStatusSelected('');
    props?.history.push({ search: newParam.toString() });
  };

  const handleClearPlannedStatus = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.delete('plannedStatus');
    setPlannedStatusSelected('');
    props?.history.push({ search: newParam.toString() });
  };

  const handleClearState = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.delete('state');
    setStateSelected(initState);
    props?.history.push({ search: newParam.toString() });
  };

  const handleClearFacility = () => {
    const newParam = new URLSearchParams(props?.location?.search);
    newParam.delete('facility');
    setFacilitySelected(initFacility);
    props?.history.push({ search: newParam.toString() });
  };

  const fetchFacilityList = async () => {
    setFacilityLoading(true);
    try {
      const res = await getFacilityByUserId(userId);
      if (res.status === 200) setFacilityList(res.data);
    } catch (error) {
      openNotification({ type: 'error', message: 'Error loading facilities' });
    } finally {
      setFacilityLoading(false);
    }
  };

  const fetchStateList = async () => {
    // if (userCategory === userCats.EDM) {
    setStateLoading(true);
    try {
      // const res = await getStates();
      const res = await getStatePersonalizedV2();
      if (res?.result?.status?.code === '00') setStateList(res?.result?.data);
    } catch (error) {
      openNotification({ type: 'error', message: 'Error loading states' });
    } finally {
      setStateLoading(false);
    }
    // }
  };

  const renderContent = () => {
    const CommonProps = {
      props,
      history,
      reportYear: year,
      reportType: type,
      reportSearch: search,
      reportStatus: status,
      facilityId,
      stateId,
      reportQuarter: quarter,
      plannedStatus,
    };
    return type === reportTypes.QAQC?.toLowerCase() ? (
      <AllResultQaqc {...CommonProps} />
    ) : (
      <AllResultNormal {...CommonProps} />
    );
  };

  const activeQuarterLabel = reportquarters.find((q) => q.value.toString() === quarter)?.label;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* LEFT SIDE: TOGGLES */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              value={year}
              size="small"
              onChange={(e) => handleSelectResultYear(e.target.value)}
              startAdornment={
                <CalendarTodayOutlined sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              }
              sx={{ borderRadius: '10px', minWidth: 100, fontWeight: 600 }}
            >
              {years?.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>

            <ButtonGroup
              disableElevation
              variant="outlined"
              sx={{ borderRadius: '10px', overflow: 'hidden', height: 40 }}
            >
              {[reportTypes.QAQC, reportTypes.NORMAL].map((t) => (
                <Button
                  key={t}
                  onClick={() => handleSelectResultType(t.toLowerCase())}
                  sx={{
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: type === t.toLowerCase() ? 'primary.main' : 'transparent',
                    color: type === t.toLowerCase() ? 'white' : 'primary.main',
                    '&:hover': {
                      bgcolor: type === t.toLowerCase() ? 'primary.dark' : alpha('#1976d2', 0.05),
                    },
                  }}
                >
                  {t}
                </Button>
              ))}
            </ButtonGroup>
          </Stack>

          {/* RIGHT SIDE: UNIVERSAL QUARTER CHIPS & ADVANCED FILTER */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              {reportquarters.map((q) => {
                const isSelected = quarter === q.value.toString();
                return (
                  <Chip
                    key={q.value}
                    label={q.label}
                    size="small"
                    clickable
                    onClick={() => handleSelectQuarter(q.value)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{
                      fontWeight: 600,
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      height: '26px',
                    }}
                  />
                );
              })}
              <Divider sx={{ height: 24, mx: 1 }} orientation="vertical" />
            </Stack>

            <Tooltip title="Advanced Filters">
              <IconButton
                size="small"
                onClick={handleFilterDropdownClick}
                color={status || plannedStatus || facilityId || stateId ? 'primary' : 'default'}
              >
                <FilterAltOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* ACTIVE FILTER CHIPS DISPLAY */}
        {(status || plannedStatus || stateId || facilityId) && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            {status && (
              <Chip
                label={`Status: ${status}`}
                onDelete={handleClearReportStatus}
                sx={{
                  bgcolor: alpha('#1976d2', 0.1),
                  color: 'primary.main',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
            {plannedStatus && (
              <Chip
                label={`Schedule: ${plannedStatus}`}
                onDelete={handleClearPlannedStatus}
                sx={{
                  bgcolor: alpha('#4a148c', 0.1),
                  color: '#4a148c',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
            {facilityId && (
              <Chip
                label={`Facility: ${facilitySelected?.facilityName || 'Selected'}`}
                onDelete={handleClearFacility}
                sx={{
                  bgcolor: alpha('#2e7d32', 0.1),
                  color: '#2e7d32',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
            {stateId && (
              <Chip
                label={`State: ${stateSelected?.stateName || 'Selected'}`}
                onDelete={handleClearState}
                sx={{
                  bgcolor: alpha('#ed6c02', 0.1),
                  color: '#ed6c02',
                  fontWeight: 700,
                  borderRadius: '8px',
                }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* FILTER MENU POPUP */}
      <Menu
        anchorEl={anchorFilterDrowpdownEl}
        open={Boolean(anchorFilterDrowpdownEl)}
        onClose={handleFilterDropdownClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mt: 1,
            width: 280,
            p: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          Filter Results
        </Typography>
        <br />
        <Stack spacing={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Schedule Status</InputLabel>
            <Select
              value={plannedStatusSelected}
              label="Schedule Status"
              onChange={(e) => setPlannedStatusSelected(e.target.value)}
              sx={{ borderRadius: '10px' }}
            >
              {Object.keys(planningStatuses).map((key) => (
                <MenuItem key={key} value={planningStatuses[key]}>
                  {planningStatuses[key].toLowerCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            options={facilityList}
            getOptionLabel={(option) => option.facilityName || ''}
            value={facilitySelected.facilityId ? facilitySelected : null}
            onChange={(e, value) => setFacilitySelected(value || initFacility)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Facility"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: facilityLoading && <CircularProgress size={16} sx={{ mr: 1 }} />,
                }}
              />
            )}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />

          {/* {userCategory === userCats.EDM && ( */}
          <Autocomplete
            options={stateList}
            getOptionLabel={(option) => option.stateName || ''}
            value={stateSelected.stateId ? stateSelected : null}
            onChange={(e, value) => setStateSelected(value || initState)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: stateLoading && <CircularProgress size={16} sx={{ mr: 1 }} />,
                }}
              />
            )}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          {/* )} */}

          <Button
            variant="contained"
            fullWidth
            onClick={handleFilter}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Menu>

      <Box sx={{ mt: 1 }}>{renderContent()}</Box>
    </Box>
  );
}
